/**
 * Shared Organization / WebSite entity nodes.
 *
 * `@id` references only resolve inside a single page's own graph — search
 * engines do not fetch another URL to dereference one. So every non-homepage
 * route that references the organisation or the website must carry the stub
 * node in its own graph. The full Organization node lives on the homepage only.
 */

export const ORGANIZATION_ID = "https://podgo.io/#organization";
export const WEBSITE_ID = "https://podgo.io/#website";

export const organizationRef = { "@id": ORGANIZATION_ID } as const;
export const websiteRef = { "@id": WEBSITE_ID } as const;

const organizationStub = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "PODGO",
  url: "https://podgo.io/",
};

const websiteStub = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "PODGO",
  url: "https://podgo.io/",
  publisher: organizationRef,
};

type Node = Record<string, unknown>;

/**
 * Corporate-suffix hint used to type a show's author as an Organization rather
 * than a Person. Fallback heuristic only: ambiguous names (a show title used as
 * the author) stay mistyped until an explicit per-show type exists.
 */
const ORG_HINT =
  /\b(LLC|L\.L\.C|Inc|Incorporated|Ltd|Limited|Corp|Corporation|Group|Holdings|Media|Network|Networks|Studio|Studios|Production|Productions|Partners)\b\.?$/i;

export function authorNode(name?: string | null, explicitType?: "Person" | "Organization") {
  const n = name?.trim();
  if (!n) return undefined;
  return { "@type": explicitType ?? (ORG_HINT.test(n) ? "Organization" : "Person"), name: n };
}

/**
 * BreadcrumbList node built from the same trail the visible <Breadcrumbs />
 * renders. The final crumb omits `item` — the current page must not link to
 * itself — and relative paths are resolved against the canonical origin.
 */
export function breadcrumbNode(items: { name: string; path?: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((crumb, i) => {
      const last = i === items.length - 1;
      return {
        "@type": "ListItem",
        position: i + 1,
        name: crumb.name.trim(),
        ...(last || !crumb.path
          ? {}
          : { item: `https://podgo.io${crumb.path === "/" ? "/" : crumb.path}` }),
      };
    }),
  };
}

/**
 * Wraps page-specific nodes in a single `@graph` that always carries the
 * Organization stub, so publisher / isPartOf references never dangle.
 */
export function pageGraph(nodes: Node[], options?: { includeWebsite?: boolean }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      organizationStub,
      ...(options?.includeWebsite ? [websiteStub] : []),
      ...nodes,
    ],
  };
}

// ---------------------------------------------------------------------------
// Studios
//
// Studios are physical premises at verified street addresses with bookable
// rooms, so `LocalBusiness` is the primary type. `Service` only ever appears
// nested as `itemOffered` inside the studio's offer catalogue.
// ---------------------------------------------------------------------------

/** Stable node id so room and hub pages can reference a studio. */
export function studioBusinessId(locationSlug: string) {
  return `https://podgo.io/studios/${locationSlug}#business`;
}

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

type Hours = Record<string, { open: string; close: string; closed?: boolean } | undefined>;

/**
 * Opening hours straight from the studio's saved settings. Days sharing the
 * same open/close pair collapse into one spec with a `dayOfWeek` array; closed
 * days are simply absent.
 */
export function openingHoursSpecification(hours?: Hours | null) {
  if (!hours) return undefined;
  const groups = new Map<string, string[]>();
  for (const day of DAY_ORDER) {
    const spec = hours[day];
    if (!spec || spec.closed || !spec.open || !spec.close) continue;
    const key = `${spec.open}-${spec.close}`;
    groups.set(key, [...(groups.get(key) ?? []), day.charAt(0).toUpperCase() + day.slice(1)]);
  }
  if (groups.size === 0) return undefined;
  return [...groups.entries()].map(([key, dayOfWeek]) => {
    const [opens, closes] = key.split("-");
    return { "@type": "OpeningHoursSpecification", dayOfWeek, opens, closes };
  });
}

interface OfferRoom {
  name: string;
  slug: string;
  hourly_rate: number;
}

/**
 * Hourly booking offer for one room. `unitCode: "HUR"` marks the rate as
 * per-hour rather than a flat price.
 */
export function roomOfferNode(
  room: OfferRoom,
  locationSlug: string,
  locationName: string,
) {
  const price = String(room.hourly_rate ?? 0);
  return {
    "@type": "Offer",
    name: room.name,
    url: `https://podgo.io/studios/${locationSlug}/${room.slug}`,
    priceCurrency: "USD",
    price,
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price,
      priceCurrency: "USD",
      unitCode: "HUR",
    },
    itemOffered: {
      "@type": "Service",
      name: `Podcast studio session, ${room.name}`,
      serviceType: "Podcast recording studio rental",
      provider: { "@id": studioBusinessId(locationSlug) },
      areaServed: locationName,
    },
  };
}

/** Deduplicated amenity list across a studio's rooms. */
export function amenityFeatures(rooms: { amenities?: string[] | null }[]) {
  const seen = new Set<string>();
  for (const room of rooms) {
    for (const raw of room.amenities ?? []) {
      const name = raw.trim().replace(/\.$/, "");
      if (name) seen.add(name);
    }
  }
  if (seen.size === 0) return undefined;
  return [...seen].map((name) => ({
    "@type": "LocationFeatureSpecification",
    name,
    value: true,
  }));
}

/** "$150/hr" or "$150–$250/hr" from the studio's published room rates. */
export function hourlyPriceRange(rooms: { hourly_rate: number }[]) {
  const rates = rooms.map((r) => Number(r.hourly_rate)).filter((n) => Number.isFinite(n) && n > 0);
  if (rates.length === 0) return undefined;
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  return min === max ? `$${min}/hr` : `$${min}–$${max}/hr`;
}

// ---------------------------------------------------------------------------
// Editorial (blog)
// ---------------------------------------------------------------------------

/**
 * Postgres timestamps carry fractional seconds ("…:13.328+00"), which is not a
 * valid schema.org Date/DateTime. Normalise to whole-second ISO 8601.
 */
export function isoDateTime(value?: string | null): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export interface BlogPostLike {
  slug: string;
  title: string;
  excerpt?: string | null;
  cover_image_url?: string | null;
  category?: string | null;
  author_name?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
}

export function blogPostUrl(slug: string) {
  return `https://podgo.io/blog/${slug}`;
}

/**
 * BlogPosting for a single article. Bylines on this site are editorial desks
 * ("PODGO Team"), never individuals, so the author is typed as an
 * Organization unless the name reads like a person.
 */
export function blogPostingNode(post: BlogPostLike, options?: { full?: boolean }) {
  const url = blogPostUrl(post.slug);
  const published = isoDateTime(post.published_at);
  const modified = isoDateTime(post.updated_at);
  const headline = post.title.trim();
  const excerpt = post.excerpt?.trim();
  const image = post.cover_image_url?.trim();

  return {
    "@type": "BlogPosting",
    "@id": `${url}#post`,
    url,
    headline,
    ...(excerpt ? { description: excerpt } : {}),
    ...(published ? { datePublished: published } : {}),
    // Only a revision on a later calendar day counts as an update; save-time
    // jitter on the publish day is not a modification worth declaring.
    ...(modified && published && modified.slice(0, 10) !== published.slice(0, 10)
      ? { dateModified: modified }
      : {}),
    ...(post.category ? { articleSection: post.category.trim() } : {}),
    ...(image ? { image: { "@type": "ImageObject", url: image } } : {}),
    ...(post.author_name
      ? { author: authorNode(post.author_name, /team|podgo/i.test(post.author_name) ? "Organization" : undefined) }
      : { author: organizationRef }),
    publisher: organizationRef,
    inLanguage: "en-US",
    ...(options?.full ? { mainEntityOfPage: { "@type": "WebPage", "@id": url }, isPartOf: BLOG_REF } : {}),
  };
}

export const BLOG_ID = "https://podgo.io/blog#blog";
export const BLOG_REF = { "@id": BLOG_ID } as const;

/** Blog node for /blog, listing the posts currently rendered on the page. */
export function blogNode(posts: BlogPostLike[]) {
  return {
    "@type": "Blog",
    "@id": BLOG_ID,
    url: "https://podgo.io/blog",
    name: "PODGO News & Updates",
    description:
      "Platform updates, industry insights, and tips for podcasters from the PODGO team.",
    publisher: organizationRef,
    inLanguage: "en-US",
    isPartOf: websiteRef,
    ...(posts.length ? { blogPost: posts.map((post) => blogPostingNode(post)) } : {}),
  };
}
